<template>
  <div>
    <ProgressBar
      v-if="!isLiteView()"
      active-step="About you"
    />
    <div>
      <PageHeading :default-heading="pageHeader" />
      <!-- Intro (Getting Started) Section -->
      <div class="pvd-container container-divider">
        <div id="intro-gs">
          <h2
            class="pvd-heading-L"
          >
            Here's what you'll need to begin
          </h2>
          <p>
            Make sure you have your Trust documents ready before you start your application, as well
            as the following information from the grantor and each trustee:
          </p>
          <ul>
            <li>Contact information (name, email, phone, and address)</li>
            <li>Date of birth</li>
            <li>Social Security or taxpayer ID number</li>
            <li>Citizenship information</li>
            <li>Employment information</li>
          </ul>
        </div>
      </div>
      <form
        ref="loginForm"
        autocomplete="off"
        method="post"
        novalidate="novalidate"
        :action="pd_envConfig.loginActionURL"
        @submit.prevent="handleFormSubmit()"
      >
        <!-- Toggle Component -->
        <div class="pvd-container container-divider">
          <h2
            id="existingCustomer"
            class="pvd-heading-L"
          >
            Are you already a Fidelity customer?
          </h2>
          <ToggleComponent
            :id-prefix="idPrefix"
            :toggle-value.sync="pu_existingCustomer"
            yes-label="Yes"
            no-label="No"
            :validation-rules="pd_content.existing_customer_validation_rules"
            supplemental-row-text="Select Yes if you have a brokerage, IRA, 401(k), or other Fidelity account."
            :label="''"
            :track-events="true"
            message="Select whether you are a Fidelity customer."
            :show-internal-error-message="'true'"
          />
        </div>

        <!-- Customer Section -->
        <div
          v-if="pu_existingCustomer === 'Yes'"
          ref="customer"
          class="pvd-container container-divider"
        >
          <h2 class="pvd-heading-L">
            Opening an account takes just a few minutes.
          </h2>
          <ul class="pvd-list-bullet">
            <li>
              First, you’ll need to log into your Fidelity account.
            </li>
            <li>
              Next, we’ll fill your application with information from your existing profile.
            </li>
            <li>
              Finally, begin the process of transferring money into your account.
            </li>
          </ul>
        </div>
        <!-- Prospect Section -->
        <div
          v-if="pu_existingCustomer === 'No'"
          id="prospect-bullets"
          ref="Prospect"
          class="pvd-container container-divider"
        >
          <div>
            <h2 class="pvd-heading-L">
              Are you returning to complete an existing trust account application?
            </h2>
            <ToggleComponent
              id-prefix="returning__toggle"
              :toggle-value.sync="isReturning"
              yes-label="Yes"
              no-label="No"
              supplemental-row-text="Select No if you're starting a new application."
              :label="''"
              :track-events="true"
              :validation-rules="returningToggleValidation"
              message="Please make a selection."
              @update:toggleValue="isReturningChanged"
            />
          </div>
        </div>
        <div
          v-if="isReturning && pu_existingCustomer === 'No'"
          class="pvd-container container-divider"
        >
          <div v-if="isReturning === 'Yes'">
            <h2 class="pvd-heading-L">
              Enter the following information to return to your application
            </h2>
            <apx-text-input
              id="prospect-ssn"
              id-prefix="trust-login-prospect-ssn"
              class="ssn"
              label="Last 4 digits of SSN"
              :validation-rules="prospectSSNValidation"
              :validation-text="prospectSSNValidationText"
              @value-changed.prevent="ssnChanged($event)"
            />

            <apx-date-select
              id="dateOfBirth"
              group-label="Date of birth"
              :month-value="month"
              month-label="Month"
              :day-value="date"
              day-label="Day"
              :year-value="year"
              year-label="Year"
              validation-text="The date of birth entered is not a valid date."
              default-select-label="Select"
              :min-date="pd_content.min_date_allowed_for_dob"
              eighteen-or-above
              required
              :date-input-value="pu_dob"
              @value-changed.prevent="dateOfBirthChanged($event)"
            />
          </div>
          <div v-if="isReturning === 'No'">
            <h2
              class="pvd-heading-L"
            >
              Opening an account takes just a few minutes
            </h2>
            <p>
              In the pages ahead, you can expect that:
            </p>
            <ul class="pvd-list-bullet">
              <li>
                First, we'll ask you some
                <a
                  id="tt-popover-trigger"
                  href="javascript:void(0);"
                  @click="track('detail_about_yourself', 'interaction')"
                >details about yourself</a>, the trust, and other people involved in the trust.
              </li>
              <li>
                Next, once the account is opened, you’ll create a username and password to log in with.
              </li>
              <li>
                Then, you can set investing goals and begin the process of transferring money into your account.
              </li>
            </ul>

            <ModalComponent
              id-val="tt-popover"
              modal-header="What you'll need to provide"
              :modal-list="['Identifying information (Social Security number, date of birth, etc.)',
                            'Contact information (legal/mailing address, email address, phone number)',
                            'Employment information']"
              modal-class="small"
            />
          </div>
        </div>
        <!-- Navigation Section -->
        <NavigationComponent :is-customer="pu_existingCustomer" />
      </form>
    </div>
  </div>
</template>

<script>

import NavigationComponent from '../components/NavigationComponent.vue';
import ToggleComponent from '../components/ToggleComponent.vue';
import ModalComponent from '../components/ModalComponent.vue';
import PageHeading from '../components/PageHeading.vue';
import dataHelper from '../mixins/dataHelper';
import utility from '../mixins/utility';
import validationHelper from '../mixins/validationHelper';
import ProgressBar from '../components/ProgressBar.vue';
import measurement from '../mixins/measurement';

export default {
  name: 'Login',
  components: {
    NavigationComponent,
    ToggleComponent,
    PageHeading,
    ProgressBar,
    ModalComponent,
  },
  mixins: [dataHelper, utility, validationHelper, measurement],
  inject: ['$validator'],
  data() {
    return {
      idPrefix: 'existingCustomer',
      backButtonAttempt: false,
      windowLocation: window.location,
      returningToggleValidation: {
        regex: '^(Yes|No)$',
        required: 'true',
      },
      prospectSSNValidation: '{"validations": {"required": "true", "max": "4", "min": "4", "regex": "^[0-9]+$"}}',
      prospectSSNValidationText: '{"validations":{"required":"Enter the last 4 digits of your SSN.","max":"You\'ve entered too many characters. Please try again.","min":"You\'ve entered too few characters. Please try again.","regex":"The number you entered is invalid. Please enter a valid SSN."}}',
      isReturning: '',
      pageTitle: 'Open a trust account',
      needHelpText: 'Need help logging in?',
    };
  },
  computed: {
    pageHeader() {
      document.title = this.setTitle(this.pageTitle);
      return this.pageTitle;
    },
    handleNeedLoggingClick() {
      return 'https://login.fidelity.com/ftgw/Fas/Fidelity/RtlCust/IdentifyUser/Init/?';
    },

    authorizationUrl() {
      const redirectQuery = '?AuthOrigUrl=';
      let redirectUrl = `${this.pd_envConfig.loginAuthzURL}${redirectQuery}${this.pd_envConfig.retailAccountSelectorURL}?rt=${this.routeId}`;

      if (this.pd_envConfig.supportedCustomers.includes(this.routeId)) {
        // For trust and other supported customers, redirect back to the current flow
        const returnUrl = `${this.windowLocation.origin}${this.windowLocation.pathname}${this.windowLocation.hash}`;
        redirectUrl = `${this.windowLocation.origin}${this.pd_envConfig.loginAuthzPath}${redirectQuery}${encodeURIComponent(returnUrl)}`;
      }
      return redirectUrl;
    },
    month() {
      if (this.pu_dob) {
        const splitDob = this.pu_dob.split('/');
        return splitDob[0];
      }
      return '';
    },
    date() {
      if (this.pu_dob) {
        const splitDob = this.pu_dob.split('/');
        return splitDob[1];
      }
      return '';
    },
    year() {
      if (this.pu_dob) {
        const splitDob = this.pu_dob.split('/');
        return splitDob[2];
      }
      return '';
    },
  },
  mounted() {
    this.track(this.cs_stateName || 'login', 'pageview');
    this.removeModalClassIfExists();
    this.configureFirstPageBackButton(
      () => {
        if (this.pd_envConfig.accountOverview) {
          document.location = this.pd_envConfig.accountOverview;
        }
      },
    );
    document.title = this.setTitle(this.pageTitle);
  },
  methods: {
    handleFormSubmit() {
      this.$validator.validateScopes()
        .then((isValid) => {
          if (isValid) {
            // For trust accounts, redirect to digital login directly instead of form POST
            // This ensures the hash-based routing state is preserved after authentication
            // Uses the same login flow as Welcome Back (which works correctly)
            if (this.routeId === 'trust') {
              const returnUrl = `${this.windowLocation.origin}${this.windowLocation.pathname}${this.windowLocation.hash}`;
              const baseLoginUrl = this.pd_envConfig.welcomeBackloginURL.split('?')[0];
              const loginUrl = `${baseLoginUrl}?AuthRedUrl=${encodeURIComponent(returnUrl)}`;
              window.location.assign(loginUrl);
            } else {
              this.$refs.loginForm.submit();
            }
          } else if (this.errors && this.errors.items[0]) {
            if (this.errors.items[0].field === 'SSN') {
              this.$refs.SSN.focus();
            }
            if (this.errors.items[0].field === 'PIN') {
              this.$refs.PIN.focus();
            }
          }
        });
    },
    isReturningChanged() {
      this.commitPrimaryUserData('', 'ssn');
      this.commitPrimaryUserData('', 'dob');
    },
    ssnChanged(event) {
      let ssn = event.detail.value;
      if (ssn) {
        ssn = ssn.replace(/[^0-9]/g, '');
      }
      if (ssn !== event.detail.value) {
        const $input = event.target.querySelector('#trust-login-prospect-ssn-input');
        $input.value = ssn;
      }
      this.commitPrimaryUserData(ssn, 'ssn');
    },
    dateOfBirthChanged(event) {
      this.commitPrimaryUserData(`${event.detail.month}/${event.detail.day}/${event.detail.year}`, 'dob');
    },
  },
};
</script>

<style scoped>
apx-text-input >>> #trust-login-prospect-ssn-input {
  width: 10em;
}
</style>
